import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";

import { selectFolderById, useGetFolderQuery, useGetMultiProjectQuery, useGetReportQuery } from "fond/api";
import { getCurrentProject } from "fond/project";
import { useAppSelector } from "fond/utils/hooks";

export type SearchScope = {
  type: "workspace" | "folder" | "recent" | "shared" | "starred" | "market_lens";
  folderId: string | null;
  folderName: string | null;
};

export type UseSearchScopeReturn = {
  availableScopes: SearchScope[];
  activeScope: SearchScope;
  setActiveScope: (scope: SearchScope) => void;
};

const WORKSPACE_SCOPE: SearchScope = { type: "workspace", folderId: null, folderName: null };
const RECENT_SCOPE: SearchScope = { type: "recent", folderId: null, folderName: null };
const SHARED_SCOPE: SearchScope = { type: "shared", folderId: null, folderName: null };
const STARRED_SCOPE: SearchScope = { type: "starred", folderId: null, folderName: null };

export const useSearchScope = (): UseSearchScopeReturn => {
  const { pathname } = useLocation();
  const {
    folderId,
    uuid: projectId,
    reportId,
    multiProjectFolderId,
    cityId,
  } = useParams<"folderId" | "uuid" | "reportId" | "multiProjectFolderId" | "cityId">();

  const project = useAppSelector((state) => getCurrentProject(state.project));
  const { data: report } = useGetReportQuery(reportId ?? "", { skip: !reportId });

  // For /city/:cityId, fetch the MultiProject to get its folder ID (already cached by entity guard)
  const { data: multiProject } = useGetMultiProjectQuery(cityId ?? "", { skip: !cityId });

  // The Market Lens folder ID comes from either the /cities/:multiProjectFolderId route
  // or from the MultiProject's Folder on the /city/:cityId route
  const marketLensFolderId = multiProjectFolderId ?? multiProject?.Folder?.ID ?? null;

  // Determine the contextual folder ID based on the current route
  const contextFolderId = useMemo(() => {
    if (folderId) return folderId;
    if (projectId && project && project.EntityType === "project" && project.FolderID) return project.FolderID;
    if (reportId && report?.Folder?.ID) return report.Folder.ID;
    if (marketLensFolderId) return marketLensFolderId;
    return null;
  }, [folderId, projectId, project, reportId, report, marketLensFolderId]);

  const contextFolder = useAppSelector((state) => (contextFolderId ? selectFolderById(state, contextFolderId) : undefined));

  // For Market Lens folders, use the singular getFolder query (already cached by the entity guard)
  // to get the Parent object directly, rather than relying on the getFolders list cache.
  const { data: marketLensFolderData } = useGetFolderQuery(marketLensFolderId ?? "", { skip: !marketLensFolderId });
  const isMarketLensFolder = Boolean(marketLensFolderData?.MultiProject);

  const marketLensFolderName = marketLensFolderData?.Name ?? null;

  const availableScopes = useMemo<SearchScope[]>(() => {
    const scopes: SearchScope[] = [WORKSPACE_SCOPE];
    if (contextFolderId && isMarketLensFolder) {
      scopes.push({ type: "market_lens", folderId: contextFolderId, folderName: marketLensFolderName });
    } else if (contextFolderId) {
      scopes.push({ type: "folder", folderId: contextFolderId, folderName: contextFolder?.Name ?? null });
    }
    scopes.push(RECENT_SCOPE, STARRED_SCOPE, SHARED_SCOPE);
    return scopes;
  }, [contextFolderId, isMarketLensFolder, marketLensFolderName, contextFolder?.Name]);

  const defaultScope = useMemo<SearchScope>(() => {
    if (pathname.startsWith("/recent")) return RECENT_SCOPE;
    if (pathname.startsWith("/shared")) return SHARED_SCOPE;
    if (pathname.startsWith("/starred")) return STARRED_SCOPE;
    return WORKSPACE_SCOPE;
  }, [pathname]);

  const deriveScope = useCallback((): SearchScope => {
    if (contextFolderId && isMarketLensFolder) {
      return { type: "market_lens", folderId: contextFolderId, folderName: marketLensFolderName };
    }
    if (contextFolderId) {
      return { type: "folder", folderId: contextFolderId, folderName: contextFolder?.Name ?? null };
    }
    return defaultScope;
  }, [contextFolderId, isMarketLensFolder, marketLensFolderName, contextFolder?.Name, defaultScope]);

  const [activeScope, setActiveScope] = useState<SearchScope>(deriveScope);

  // Update active scope when the context changes (e.g. navigating to a different folder/project)
  useEffect(() => {
    setActiveScope(deriveScope());
  }, [deriveScope]);

  return { availableScopes, activeScope, setActiveScope };
};
